import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';

// Atalho curto digitável: /relatorios/:code → /gestao/relatorios/:code
function RelatorioAtalho() {
  const { code } = useParams();
  return <Navigate to={`/gestao/relatorios/${code}`} replace />;
}
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { DataProvider, useData } from './contexts/DataContext.jsx';
import { AvecSyncProvider } from './contexts/AvecSyncContext.jsx';
import { PerformanceProvider } from './contexts/PerformanceContext.jsx';
import { AchievementsProvider } from './contexts/AchievementsContext.jsx';
import VersionChecker from './components/VersionChecker.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import NotificationsGate from './components/NotificationsGate.jsx';
import Layout from './components/Layout.jsx';
import FKLoader from './components/FKLoader.jsx';
import { useOperadorFinanceiro } from './utils/useOperadorFinanceiro.js';

// ─── Eager (sempre no bundle inicial) ────────────────────────────────────
// Login: primeira tela que todo mundo bate. Manter eager evita "flash" de
// fallback antes do render.
import Login from './pages/Login.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import { podeReunioes } from './config/reunioesAcesso.js';
import { podeManualRecepcao } from './config/manualAcesso.js';
import { IA_ATENDENTE_MENU } from './config/featureFlags.js';
import { CRM_SOFIA_MENU } from './config/featureFlags.js';
import { OCULTAR_PRIVATES } from './config/featureFlags.js';
const BaterPonto = lazy(() => import('./pages/BaterPonto.jsx'));

// ─── Lazy (chunk próprio, baixa só quando user navega) ──────────────────
// Cada página vira um arquivo separado. Bundle inicial cai de ~2MB pra
// ~600KB. Ganho gigante em redes lentas e primeiro carregamento.
const PublicReview        = lazy(() => import('./pages/PublicReview.jsx'));
const DesignSystem        = lazy(() => import('./pages/DesignSystem.jsx'));
const PrivacyPolicy       = lazy(() => import('./pages/PrivacyPolicy.jsx'));
const Support             = lazy(() => import('./pages/Support.jsx'));
const AdminDashboard      = lazy(() => import('./pages/admin/Dashboard.jsx'));
const AdminUnits          = lazy(() => import('./pages/admin/Units.jsx'));
const AdminProfessionals  = lazy(() => import('./pages/admin/Professionals.jsx'));
const AdminClientes       = lazy(() => import('./pages/admin/Clientes.jsx'));
const AdminAgenda         = lazy(() => import('./pages/admin/Agenda.jsx'));
const AdminEstoque        = lazy(() => import('./pages/admin/Estoque.jsx'));
const AdminReports        = lazy(() => import('./pages/admin/Reports.jsx'));
const AdminReportDetail   = lazy(() => import('./pages/admin/reports/ReportDetail.jsx'));
const AdminValidacaoNativa = lazy(() => import('./pages/admin/ValidacaoNativa.jsx'));
const AdminServices       = lazy(() => import('./pages/admin/Services.jsx'));
const AdminGoals          = lazy(() => import('./pages/admin/Goals.jsx'));
const NaRegua             = lazy(() => import('./pages/admin/NaRegua.jsx'));
const DNA360              = lazy(() => import('./pages/admin/DNA360.jsx'));
const AdminReunioes       = lazy(() => import('./pages/admin/Reunioes.jsx'));
const AdminCandidatos     = lazy(() => import('./pages/admin/Candidatos.jsx'));
const PrivatesApp         = lazy(() => import('./pages/admin/privates/PrivatesApp.jsx'));
const AdminSettings       = lazy(() => import('./pages/admin/Settings.jsx'));
const ProfessionalDashboard = lazy(() => import('./pages/professional/Dashboard.jsx'));
const AdminRanking        = lazy(() => import('./pages/admin/Ranking.jsx'));
const AdminRankingUnidade = lazy(() => import('./pages/admin/RankingUnidade.jsx'));
const AdminChallenges     = lazy(() => import('./pages/admin/Challenges.jsx'));
const AdminEngagement     = lazy(() => import('./pages/admin/Engagement.jsx'));
const AvecDebug           = lazy(() => import('./pages/admin/AvecDebug.jsx'));
const MyAchievements      = lazy(() => import('./pages/professional/MyAchievements.jsx'));
const MeuPonto            = lazy(() => import('./pages/professional/MeuPonto.jsx'));
const GerenteDashboard    = lazy(() => import('./pages/manager/Dashboard.jsx'));
const DuelsPage           = lazy(() => import('./pages/Duels.jsx'));
const Aniversarios        = lazy(() => import('./pages/Aniversarios.jsx'));
const Wrapped             = lazy(() => import('./pages/Wrapped.jsx'));
const AdminFinance        = lazy(() => import('./pages/admin/Finance.jsx'));
const AdminPonto          = lazy(() => import('./pages/admin/Ponto.jsx'));
const RecibosFolha        = lazy(() => import('./pages/admin/RecibosFolha.jsx'));
const AdminAuditoria      = lazy(() => import('./pages/admin/Auditoria.jsx'));
const AdminUsuarios       = lazy(() => import('./pages/admin/Usuarios.jsx'));
const AdminAvaliacoes     = lazy(() => import('./pages/admin/Avaliacoes.jsx'));
const AdminIntegracoes    = lazy(() => import('./pages/admin/Integracoes.jsx'));
const AdminAtendimento    = lazy(() => import('./pages/admin/Atendimento.jsx'));
const AdminImportHistorico = lazy(() => import('./pages/admin/ImportHistorico.jsx'));
const AdminInteligencia   = lazy(() => import('./pages/admin/Inteligencia.jsx'));
// IA de atendimento, modo teste (13/10/2026): simulador do master + fila da recepção
const AtendimentoSimulador = lazy(() => import('./pages/admin/atendimento/Simulador.jsx'));
const PreAgendamentos     = lazy(() => import('./pages/admin/atendimento/PreAgendamentos.jsx'));
// CRM da Sofia, fase 1 (24/10/2026): leads, funil, conversas e qualidade da Sofia (só master)
const CrmSofia            = lazy(() => import('./pages/admin/crm/CrmSofia.jsx'));
// Manual da Recepção dentro do app (14/10/2026): deck em tela cheia, retrato no celular
const ManualRecepcao      = lazy(() => import('./pages/ManualRecepcao.jsx'));

// Fallback enquanto chunk de rota baixa: logo Feel King + quantum,
// centralizado na área de conteúdo (Layout já está renderizado, sem forçar
// bg escuro pra não dar flash).
function RouteFallback() {
  return <FKLoader className="min-h-[60vh]" />;
}

// Financeiro completo (06/10/2026 — lista fechada): só o OPERADOR financeiro,
// e quem diz é o SERVIDOR (useOperadorFinanceiro → /api/folha-recibos
// ?op=acesso; o master nem espera). Enquanto confere: loader (nada do
// Financeiro aparece pra quem não é operador). Não é operador → volta pro
// dashboard (mesmo padrão das páginas só-master). Falha ao conferir ≠ "não é
// operador": aviso + tentar de novo (o hook também re-tenta sozinho).
// aguardarCadastro: a conta tem cadastro ligado e o cadastro ainda não chegou
// (aparelho novo) — sem o cargo, a recepção cairia aqui e seria mandada embora
// do Caixa; então quem não é operador espera o cadastro antes de redirecionar.
function SoOperadorFinanceiro({ children, aguardarCadastro = false }) {
  const { loading, operador, erro, recarregar } = useOperadorFinanceiro();
  if (operador) return children;
  if (erro) {
    return (
      <div className="max-w-lg mx-auto mt-10 flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-900">
        <span className="text-amber-600 text-lg leading-none mt-0.5">⚠️</span>
        <div className="flex-1">
          <strong>Não deu para conferir o seu acesso ao Financeiro agora.</strong>{' '}
          {loading ? 'Tentando de novo…' : 'Verifique a conexão e tente de novo.'}
          <div className="text-[11px] text-amber-700 mt-1 break-all">{String(erro)}</div>
        </div>
        <button
          onClick={() => recarregar()}
          disabled={loading}
          className="shrink-0 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-2 rounded-lg"
        >
          Tentar de novo
        </button>
      </div>
    );
  }
  if (loading || aguardarCadastro) return <RouteFallback />;
  return <Navigate to="/gestao/dashboard" replace />;
}

function AppRoutes() {
  const { user, loading, mustChangePassword, isMaster } = useAuth();
  const dataCtx = useData();
  const professionals = dataCtx?.professionals || [];
  // Gerente = user com role=admin (acesso total) cujo professional record tem role='Gerente'.
  // Cai em /meu-desempenho ao logar (a unidade dela é o "desempenho dela").
  const myProf = user?.professionalId ? professionals.find(p => p.id === user.professionalId) : null;
  const isGerente = myProf?.role === 'Gerente';
  const isSocio = myProf?.role === 'Sócio';
  // Recepcionista — acesso bem restrito: agenda + caixas/comandas da unidade
  const isRecepcionista = myProf?.role === 'Recepcionista';
  // Marketing — só lê Relatórios, Ranking e Duelos (todas unidades). Read-only.
  const isMarketing = myProf?.role === 'Marketing';
  // Conta com cadastro ligado e cadastro ainda não carregado (aparelho novo):
  // o cargo ainda é desconhecido (ver SoOperadorFinanceiro).
  const cadastroPendente = !!user?.professionalId && professionals.length === 0;

  if (loading) return <FKLoader fullScreen />;
  // Rota pública /avaliacao/:slug — clientes acessam sem login
  if (!user) return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/avaliacao/:slug" element={<PublicReview />} />
        <Route path="/privacidade" element={<PrivacyPolicy />} />
        <Route path="/suporte" element={<Support />} />
        <Route path="/design" element={<DesignSystem />} />
        <Route path="/redefinir-senha/:token" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    </Suspense>
  );
  if (mustChangePassword) return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/avaliacao/:slug" element={<PublicReview />} />
        <Route path="/privacidade" element={<PrivacyPolicy />} />
        <Route path="/suporte" element={<Support />} />
        <Route path="/redefinir-senha/:token" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    </Suspense>
  );
  // Default landing por role — usado em /, /gestao e em redirects internos.
  const defaultLanding = isRecepcionista
    ? '/gestao/agenda'
    : user.role === 'admin'
      ? (isGerente ? '/gestao/meu-desempenho' : '/gestao/dashboard')
      : '/gestao/meu-desempenho';
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* ── Rotas públicas (sem layout admin) ────────────────────────── */}
      <Route path="/avaliacao/:slug" element={<PublicReview />} />
      <Route path="/privacidade" element={<PrivacyPolicy />} />
      <Route path="/suporte" element={<Support />} />
      {/* Obs: o app cliente vive em outro projeto (apps/cliente/) com deploy
          próprio (subdomínio dedicado). Esse repo só serve o gestão. */}

      {/* ── Redirects de retrocompatibilidade ────────────────────────── */}
      {/* URLs antigas (/dashboard, /agenda, etc.) → /gestao/<mesmo>.
          Mantém PWAs instalados e bookmarks funcionando. */}
      {[
        'dashboard','meu-desempenho','profissionais','clientes','agenda','estoque',
        'metas','desafios','engajamento','ranking','ranking-unidade','duelos','unidades','relatorios',
        'servicos','configuracoes','avec-debug','financeiro','banco-horas','auditoria','usuarios',
        'atendimento','avaliacoes','integracoes','minhas-conquistas','meta-realizado',
        'wrapped','aniversarios','inteligencia','conselheira',
        // ponto: lembretes push já entregues abrem /bater-ponto
        'bater-ponto','meu-ponto',
      ].map(p => (
        <Route key={`compat-${p}`} path={`/${p}`} element={<Navigate to={`/gestao/${p}`} replace />} />
      ))}

      {/* ── Sistema interno em /gestao ───────────────────────────────── */}
      <Route path="/gestao" element={<Layout />}>
        {user.role === 'admin' ? (
          <>
            {/* Default: Recepcionista→agenda, Gerente→meu-desempenho, admin/Marketing/Assist.Adm→dashboard. */}
            <Route index element={<Navigate to={
              isRecepcionista ? '/gestao/agenda'
              : isGerente ? '/gestao/meu-desempenho'
              : '/gestao/dashboard'
            } replace />} />
            <Route path="dashboard" element={(isRecepcionista) ? <Navigate to="/gestao/agenda" replace /> : <AdminDashboard />} />
            {/* Inteligência — análise semanal + chat (admin/master/gerente/sócio). Fora pra recepção
                e marketing: expõe faturamento e comissão individual de todo mundo. */}
            <Route path="inteligencia" element={(isRecepcionista || isMarketing) ? <Navigate to="/gestao/relatorios" replace /> : <AdminInteligencia />} />
            {/* Nome antigo da página — quem tiver o link salvo cai no novo */}
            <Route path="conselheira" element={<Navigate to="/gestao/inteligencia" replace />} />
            {/* Gerentes: página própria de "meu desempenho" + acesso restrito */}
            <Route path="meu-desempenho" element={<GerenteDashboard />} />
            <Route path="profissionais" element={(isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminProfessionals />} />
            <Route path="clientes" element={(isGerente || isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminClientes />} />
            <Route path="agenda" element={isSocio ? <Navigate to="/gestao/relatorios" replace /> : <AdminAgenda />} />
            <Route path="estoque" element={(isGerente || isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminEstoque />} />
            {/* Recepcionista VÊ o Meta x Realizado da própria unidade (pedido
                do dono 22/08/2026) — a página já escopa e limita edição */}
            <Route path="metas" element={<AdminGoals />} />
            {/* Na Régua — concierge de retorno (01/09/2026). Recepção envia as
                mensagens; admin/gerente acompanham. Marketing fora (PII). */}
            <Route path="radar" element={isMarketing ? <Navigate to="/gestao/relatorios" replace /> : <NaRegua />} />
            {/* DNA 360 — piloto SÓ MASTER (dono, 02/09/2026); API tem o mesmo portão */}
            <Route path="dna-360" element={!isMaster ? <Navigate to="/gestao/dashboard" replace /> : <DNA360 />} />
            {/* Reuniões — hub das apresentações HTML, SÓ MASTER (dono, 27/09/2026) */}
            <Route path="reunioes" element={!(isMaster || podeReunioes(user, isGerente)) ? <Navigate to="/gestao/dashboard" replace /> : <AdminReunioes />} />
            {/* Candidatos — candidaturas do site de vagas + análise da IA, SÓ MASTER (dono, 05/10/2026);
                os endpoints /api/candidatos-* têm o mesmo portão no servidor */}
            <Route path="candidatos" element={!isMaster ? <Navigate to="/gestao/dashboard" replace /> : <AdminCandidatos />} />
            {/* IA de atendimento, MODO TESTE (13/10/2026, flag IA_ATENDENTE_MENU): o Simulador é
                SÓ MASTER; os Pré-agendamentos abrem para quem o SERVIDOR liberar
                (/api/atendimento-fila?op=me: rollout IA_ATENDENTE + cargo/unidade). */}
            {IA_ATENDENTE_MENU && <Route path="atendimento-ia" element={!isMaster ? <Navigate to="/gestao/dashboard" replace /> : <AtendimentoSimulador />} />}
            {IA_ATENDENTE_MENU && <Route path="pre-agendamentos" element={(isSocio || isMarketing) ? <Navigate to="/gestao/relatorios" replace /> : <PreAgendamentos />} />}
            {/* CRM da Sofia (24/10/2026, flag CRM_SOFIA_MENU). 26/10/2026, decisão do dono: abre para a
                gerente (só as unidades dela); quem decide e recorta é o SERVIDOR (/api/crm-sofia, env
                CRM_SOFIA, padrão 'unidade'). Sócio e Marketing ficam fora, como nos Pré-agendamentos. */}
            {CRM_SOFIA_MENU && <Route path="crm-sofia" element={(isSocio || isMarketing) ? <Navigate to="/gestao/relatorios" replace /> : <CrmSofia />} />}
            <Route path="na-regua" element={<Navigate to="/gestao/radar" replace />} />
            {/* Feel King Private (02/10/2026) — permissões no servidor (/api/privates op=me).
                16/10/2026 (OCULTAR_PRIVATES): escondido de todos; só o master abre pelo link
                (o servidor responde 403 modulo_oculto para os demais). */}
            <Route path="privates/*" element={(OCULTAR_PRIVATES && !isMaster) ? <Navigate to={defaultLanding} replace /> : <PrivatesApp />} />
            <Route path="desafios" element={(isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminChallenges />} />
            <Route path="engajamento" element={(isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminEngagement />} />
            <Route path="ranking" element={<AdminRanking />} />
            <Route path="ranking-unidade" element={<AdminRankingUnidade />} />
            <Route path="bater-ponto" element={<BaterPonto />} />
            <Route path="duelos" element={<DuelsPage />} />
            <Route path="aniversarios" element={<Aniversarios />} />
            {/* Páginas SÓ pra admin puro */}
            <Route path="unidades" element={(isGerente || isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminUnits />} />
            <Route path="relatorios" element={(isGerente || isRecepcionista) ? <Navigate to="/gestao/agenda" replace /> : <AdminReports />} />
            <Route path="relatorios/validacao-nativa" element={!isMaster ? <Navigate to="/gestao/dashboard" replace /> : <AdminValidacaoNativa />} />
            {/* Página própria de cada relatório numerado (001, 002, …). A rota
                estática validacao-nativa acima vence a dinâmica :code. */}
            <Route path="relatorios/:code" element={(isGerente || isRecepcionista) ? <Navigate to="/gestao/agenda" replace /> : <AdminReportDetail />} />
            <Route path="servicos" element={(isGerente || isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminServices />} />
            <Route path="configuracoes" element={
              isGerente ? <Navigate to="/gestao/meu-desempenho" replace />
              : isSocio ? <Navigate to="/gestao/dashboard" replace />
              : isRecepcionista ? <Navigate to="/gestao/agenda" replace />
              : !isMaster ? <Navigate to="/gestao/dashboard" replace />
              : <AdminSettings />
            } />
            <Route path="avec-debug" element={(isGerente || isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AvecDebug />} />
            {/* Financeiro (06/10/2026 — lista fechada no servidor): a recepção segue
                com Caixas/Comandas (pelo cargo); o resto da gestão só se o servidor
                disser que a conta é operadora (master + contas liberadas). Conta admin
                que não é operador (ex.: Assistente Adm) → dashboard. */}
            <Route path="financeiro" element={
              (isGerente || isSocio || isMarketing) ? <Navigate to="/gestao/relatorios" replace />
              : isRecepcionista ? <AdminFinance />
              : <SoOperadorFinanceiro aguardarCadastro={cadastroPendente}><AdminFinance /></SoOperadorFinanceiro>
            } />
            {/* Banco de Horas: a GERENTE cai numa página própria só com os recibos
                da folha das unidades dela (05/10/2026 — o servidor decide quem
                vê dados; gerente não liberada vê o aviso, sem dados). */}
            <Route path="banco-horas" element={
              (isSocio || isMarketing || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace />
              : isGerente ? <RecibosFolha />
              : <AdminPonto />
            } />
            {/* Auditoria (auth_log): só a direção — /api/auth-log-list é só master (06/10/2026). */}
            <Route path="auditoria" element={!isMaster ? <Navigate to="/gestao/relatorios" replace /> : <AdminAuditoria />} />
            <Route path="usuarios" element={(isGerente || isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminUsuarios />} />
            <Route path="atendimento" element={(isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : <AdminAtendimento />} />
            <Route path="avaliacoes" element={isRecepcionista ? <Navigate to="/gestao/relatorios" replace /> : <AdminAvaliacoes />} />
            <Route path="integracoes" element={(isGerente || isSocio || isRecepcionista) ? <Navigate to="/gestao/relatorios" replace /> : !isMaster ? <Navigate to="/gestao/dashboard" replace /> : <AdminIntegracoes />} />
            <Route path="import-historico" element={!isMaster ? <Navigate to="/gestao/dashboard" replace /> : <AdminImportHistorico />} />
          </>
        ) : (
          <>
            <Route index element={<Navigate to={isRecepcionista ? '/gestao/agenda' : '/gestao/meu-desempenho'} replace />} />
            {/* Gerente cujo user.role ainda é 'professional' (legado) cai no GerenteDashboard
                igualmente — dashboard "individual" não faz sentido pra ela. */}
            <Route path="meu-desempenho" element={isGerente ? <GerenteDashboard /> : <ProfessionalDashboard />} />
            {/* Recepcionista pode ter sido cadastrada como 'professional' (legado) — abre
                agenda e financeiro mesmo assim. */}
            {isRecepcionista && <Route path="agenda" element={<AdminAgenda />} />}
            {isRecepcionista && <Route path="financeiro" element={<AdminFinance />} />}
            {isRecepcionista && <Route path="radar" element={<NaRegua />} />}
            {isRecepcionista && <Route path="na-regua" element={<Navigate to="/gestao/radar" replace />} />}
            {isRecepcionista && <Route path="privates/*" element={OCULTAR_PRIVATES ? <Navigate to={defaultLanding} replace /> : <PrivatesApp />} />}
            {/* Fila da assistente virtual (o servidor decide se libera; ver ramo admin) */}
            {IA_ATENDENTE_MENU && isRecepcionista && <Route path="pre-agendamentos" element={<PreAgendamentos />} />}
            {/* CRM da Sofia para a recepção (26/10/2026): só a unidade dela (o servidor decide) */}
            {CRM_SOFIA_MENU && isRecepcionista && <Route path="crm-sofia" element={<CrmSofia />} />}
            {/* Ranking unificado: profissional vê o mesmo "Ranking Geral" do admin
                (todas as categorias da rede), com a própria posição destacada. */}
            <Route path="ranking" element={<AdminRanking />} />
            <Route path="ranking-unidade" element={<AdminRankingUnidade />} />
            <Route path="bater-ponto" element={<BaterPonto />} />
            <Route path="duelos" element={<DuelsPage />} />
            <Route path="aniversarios" element={<Aniversarios />} />
            <Route path="minhas-conquistas" element={<MyAchievements />} />
            <Route path="meta-realizado" element={<AdminGoals />} />
          </>
        )}
        {/* Replay mensal (Wrapped) — disponível pra todos com professionalId */}
        <Route path="wrapped" element={<Wrapped />} />
        {/* Meu Ponto — banco de horas próprio (server filtra pela sessão) */}
        <Route path="meu-ponto" element={<MeuPonto />} />
        {/* Manual da Recepção (14/10/2026): Recepcionista e Gerente (admin ou
            professional) e o master. SEMPRE registrada e esperando o cadastro:
            num aparelho novo o cargo chega depois, e o VersionChecker recarrega
            nesta mesma URL; quem está lendo não pode ser jogada para fora. */}
        <Route path="manual-recepcao" element={
          cadastroPendente ? <RouteFallback />
          : podeManualRecepcao({ cargo: myProf?.role, isMaster }) ? <ManualRecepcao />
          : <Navigate to={defaultLanding} replace />
        } />
      </Route>

      {/* ── Root e catch-all ─────────────────────────────────────────── */}
      {/* Atalho: feelking.app/relatorios/001 → /gestao/relatorios/001 */}
      <Route path="/relatorios/:code" element={<RelatorioAtalho />} />
      <Route path="/" element={<Navigate to={defaultLanding} replace />} />
      <Route path="*" element={<Navigate to={defaultLanding} replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      {/* VersionChecker fica fora dos providers — funciona mesmo no Login
          e mesmo se algum provider quebrar. Detecta nova versão a cada 30s
          e auto-recarrega sem precisar de cache-clear manual. */}
      <VersionChecker />
      <AuthProvider>
        <DataProvider>
          <AvecSyncProvider>
            <PerformanceProvider>
              <AchievementsProvider>
                <NotificationsGate>
                  <ErrorBoundary>
                    <AppRoutes />
                  </ErrorBoundary>
                </NotificationsGate>
              </AchievementsProvider>
            </PerformanceProvider>
          </AvecSyncProvider>
        </DataProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
